// Machine record access for the /maintunits portal.
//
// Real records live in Firestore, which is DENY-ALL to clients (see firestore.rules).
// The only way in is the maintenance service, which verifies one customer PIN and issues
// a short-lived session scoped to that customer's fleet. Consequences worth knowing:
//
//   * No customer data is ever bundled into the JS, and no PIN is compared in the
//     browser. Reading a record requires a round trip that can fail.
//   * The portal does not reveal whether a tag exists until the PIN is right - a
//     missing machine and a wrong PIN return the same failure, on purpose.
//   * The plaintext PIN is exchanged once and never retained; only the short-lived
//     signed fleet session is held in memory/sessionStorage.
//
// Demo units are different: they are public marketing records that open without a PIN and
// never touch the API.

import { previewMaintainedUnits } from '@/data/maintainedUnitsPreview';

const API = {
  unlock: '/api/v2/portal/unlock',
  fleet: '/api/v2/portal/fleet',
  machine: '/api/v2/portal/machine',
  hours: '/api/v2/portal/hour-reading',
  tags: '/api/v2/machine-tags',
  scanRequest: '/api/v2/scan/service-request',
};

const SESSION_KEY = 'centerfire.portal.session';
const SCOPE_KEY = 'centerfire.portal.scope';
let sessionToken = sessionStorage.getItem(SESSION_KEY) || '';
// 'fleet' opens the full portal; 'history' was minted at a basic machine's gate and can read
// nothing but that machine's history sheet. The server enforces this; the client only reads it
// to decide which screen to render.
let sessionScope = sessionStorage.getItem(SCOPE_KEY) || 'fleet';

function normalise(id) {
  return String(id || '').trim();
}

/** Demo units only. Real machines are not enumerable from the client. */
export function findDemoUnit(id) {
  const key = normalise(id);
  const hit = previewMaintainedUnits.find((u) => normalise(u.lookupNumber) === key);
  return hit ? { ...hit, isReal: false } : null;
}

async function post(url, payload) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => ({}));
  return { status: res.status, ok: res.ok && body.ok !== false, body };
}

/**
 * Exchange a tag or portal token + customer PIN for a fleet-scoped session.
 */
export async function unlockPortal({ tag, portalToken, machineToken, pin }) {
  try {
    const { status, ok, body } = await post(API.unlock, { tag: tag ? normalise(tag) : undefined, portalToken, machineToken, pin });
    if (ok && body.token) {
      sessionToken = body.token;
      sessionScope = body.scope === 'history' ? 'history' : 'fleet';
      sessionStorage.setItem(SESSION_KEY, sessionToken);
      sessionStorage.setItem(SCOPE_KEY, sessionScope);
      return { scope: sessionScope };
    }
    if (status === 429) {
      return { error: 'Too many attempts. Please wait a few minutes and try again.' };
    }
    // 403 and 404 are deliberately indistinguishable here.
    return { error: 'That PIN did not match this machine record. Check the number and try again.' };
  } catch (err) {
    return { error: 'We could not reach the server. Please check your connection and try again.' };
  }
}

async function portalRequest(url, options = {}) {
  if (!sessionToken) return { status: 401, ok: false, body: {} };
  const headers = new Headers(options.headers || {});
  headers.set('Authorization', `Bearer ${sessionToken}`);
  if (options.body) headers.set('Content-Type', 'application/json');
  const response = await fetch(url, { ...options, headers });
  const body = await response.json().catch(() => ({}));
  if (response.status === 401) clearPortalSession();
  return { status: response.status, ok: response.ok, body };
}

export function hasPortalSession() {
  return Boolean(sessionToken);
}

export function portalScope() {
  return sessionToken ? sessionScope : '';
}

export function clearPortalSession() {
  sessionToken = '';
  sessionScope = 'fleet';
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(SCOPE_KEY);
}

/**
 * The caller needs the status, not just the message: 401 and 403 mean the held session cannot
 * open this machine (expired, another customer's, or history-scoped), which is recoverable by
 * re-classifying the tag. Anything else is a transient server problem worth reporting as-is.
 */
export async function loadPortalFleet() {
  try {
    const { status, ok, body } = await portalRequest(API.fleet);
    return ok ? { machines: body.machines || [] } : { status, error: 'Please enter the customer PIN to open these records.' };
  } catch {
    return { error: 'We could not reach the server. Please check your connection and try again.' };
  }
}

export async function loadPortalMachine(tag) {
  try {
    const { ok, body } = await portalRequest(`${API.machine}/${encodeURIComponent(normalise(tag))}`);
    return ok && body.machine ? { machine: body.machine } : { error: body.error || 'That machine is not in this customer fleet.' };
  } catch {
    return { error: 'We could not reach the server. Please check your connection and try again.' };
  }
}

/**
 * Ask Centerfire to look at this machine. No PIN and no session: the request records an
 * unassigned backlog task and pings Daniel. The reply is deliberately uninformative — it says
 * the same thing whether or not the machine exists, and never echoes back what was typed.
 */
export async function requestServiceOnMachine({ key, message, name, phone, company }) {
  try {
    const { status, ok } = await post(API.scanRequest, { key: normalise(key), message, name: name || undefined, phone: phone || undefined, company });
    if (ok) return {};
    if (status === 429) return { error: 'Too many requests just now. Please call us at (210) 445-4277.' };
    return { error: 'We could not send that. Please call us at (210) 445-4277.' };
  } catch {
    return { error: 'We could not reach the server. Please check your connection and try again.' };
  }
}

/**
 * The printable service history. This is the only record a history-scoped session can read,
 * and the same document the enrolled portal prints, so both tiers show an identical sheet.
 */
export async function loadHistorySheet(key) {
  try {
    const { ok, body } = await portalRequest(`${API.machine}/${encodeURIComponent(normalise(key))}/history-sheet`);
    return ok && body.sheet ? { sheet: body.sheet } : { error: body.error || 'That machine record could not be opened.' };
  } catch {
    return { error: 'We could not reach the server. Please check your connection and try again.' };
  }
}

export async function submitHourReading({ tag, hours, greased = true }) {
  try {
    const { ok, body } = await portalRequest(API.hours, { method: 'POST', body: JSON.stringify({ tag: normalise(tag), hours, greased }) });
    if (ok) return { id: body.id };
    return { error: body.error || 'We could not save that just now. Please try again.' };
  } catch (err) {
    return { error: 'We could not reach the server. Please try again.' };
  }
}

export async function undoHourReading({ tag, id }) {
  try {
    const { ok, body } = await portalRequest(API.hours, { method: 'POST', body: JSON.stringify({ tag: normalise(tag), undoId: id }) });
    if (ok) return {};
    return { error: body.error || 'We could not undo that just now. Please try again.' };
  } catch (err) {
    return { error: 'We could not reach the server. Please try again.' };
  }
}

/**
 * Live machines for the public directory: the tag, what the machine is, and whether it is
 * currently listed for sale.
 *
 * No owner, hours, serial or history. If the endpoint is unreachable the directory falls back
 * to demo tiles rather than showing an error, because a broken directory is worse than a
 * short one.
 */
export async function loadMachineTags() {
  return (await loadMachineTagsResult()).tags;
}

export async function loadMachineTagsResult() {
  try {
    const res = await fetch(API.tags, { cache: 'no-store' });
    if (!res.ok) return { tags: [], error: 'We could not check this machine record. Please try again.' };
    const body = await res.json();
    if (!Array.isArray(body.tags)) return { tags: [], error: 'The machine directory returned an invalid response.' };
    return {
      tags: body.tags.map((t) => ({
        lookupNumber: normalise(t.tag), label: t.label || null, publicType: t.type || null,
        enrolled: t.enrolled === true, kind: t.kind === 'program' ? 'program' : 'basic',
        // A boolean computed service-side. The listing's internal `ref` used to be joined
        // against here in the browser; it no longer crosses the publishing boundary at all.
        forSale: t.forSale === true,
      })),
    };
  } catch (err) {
    return { tags: [], error: 'We could not reach the server. Please check your connection and try again.' };
  }
}
